import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useTranslation } from "@/contexts/LanguageContext";
import { Mountains, Trophy, Users } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } };
function resolveUrl(u) { return u?.startsWith("/uploads") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u || ""; }

export default function AboutPage() {
  const [about, setAbout] = useState(null);
  const { lang } = useTranslation();

  useEffect(() => { axios.get(`${API}/about`).then(r => setAbout(r.data)).catch(() => {}); }, []);

  const titles = {
    page: { es: "Que es Pro Vertical?", en: "What is Pro Vertical?", fr: "Qu'est-ce que Pro Vertical?", pt: "O que e Pro Vertical?" },
    coaches: { es: "Nuestros Entrenadores", en: "Our Coaches", fr: "Nos entraineurs", pt: "Nossos treinadores" },
    athletes: { es: "Deportistas y Logros", en: "Athletes & Achievements", fr: "Athletes et realisations", pt: "Atletas e conquistas" },
  };
  const t = (k) => titles[k]?.[lang] || titles[k]?.es || k;

  if (!about) return <main className="pt-20 min-h-screen flex items-center justify-center"><p className="text-gray-400">Cargando...</p></main>;

  return (
    <main className="pt-20 min-h-screen" data-testid="about-page">
      {/* Header */}
      <section className="py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-gray-400 mb-2">Pro Vertical</p>
          <h1 className="text-4xl sm:text-5xl tracking-tight leading-none font-black uppercase font-['Cabinet_Grotesk'] mb-4" data-testid="about-title">
            {about.history_title || t("page")}
          </h1>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 lg:px-8 pb-24">
        {/* History Section */}
        {about.history_content && (
          <motion.section initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.1 } } }} className="mb-16">
            <motion.div variants={fadeUp} className="bg-[#121212] border border-white/5 p-8 mb-6">
              <p className="text-base text-gray-300 leading-relaxed whitespace-pre-wrap">{about.history_content}</p>
            </motion.div>
            {about.history_photos?.length > 0 && (
              <motion.div variants={fadeUp} className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {about.history_photos.map((photo, idx) => (
                  <div key={idx} className="aspect-[4/3] bg-[#1A1A1A] border border-white/5 overflow-hidden">
                    <img src={resolveUrl(typeof photo === "string" ? photo : photo.url)} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </motion.div>
            )}
          </motion.section>
        )}

        {/* Coaches Section */}
        {about.coaches?.length > 0 && (
          <section className="mb-16">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-gray-500 mb-6 flex items-center gap-2">
              <Users size={16} /> {t("coaches")}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {about.coaches.map((coach, idx) => (
                <motion.div key={idx} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.15 }}
                  className="bg-[#121212] border border-white/5 overflow-hidden flex flex-col sm:flex-row">
                  {coach.photo && (
                    <div className="sm:w-40 h-48 sm:h-auto flex-shrink-0 overflow-hidden">
                      <img src={resolveUrl(coach.photo)} alt={coach.name} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-5 flex-1">
                    <h3 className="text-lg font-semibold font-['Cabinet_Grotesk'] mb-1">{coach.name}</h3>
                    {coach.role && <p className="text-xs text-[#FF4D00] font-bold mb-2">{coach.role}</p>}
                    {coach.bio && <p className="text-sm text-gray-400 leading-relaxed mb-3">{coach.bio}</p>}
                    {coach.credentials && (
                      <div className="text-xs text-gray-500 whitespace-pre-wrap border-t border-white/5 pt-2">{coach.credentials}</div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* Athletes Section */}
        {about.athletes?.length > 0 && (
          <section>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-gray-500 mb-6 flex items-center gap-2">
              <Trophy size={16} /> {t("athletes")}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {about.athletes.map((athlete, idx) => (
                <motion.div key={idx} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }}
                  className="bg-[#121212] border border-white/5 overflow-hidden card-hover">
                  {athlete.photo && (
                    <div className="aspect-square overflow-hidden">
                      <img src={resolveUrl(athlete.photo)} alt={athlete.name} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-4">
                    <h4 className="text-sm font-semibold font-['Cabinet_Grotesk']">{athlete.name}</h4>
                    {athlete.achievement && <p className="text-xs text-[#FF4D00] mt-1">{athlete.achievement}</p>}
                  </div>
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* Empty state */}
        {!about.history_content && !about.coaches?.length && !about.athletes?.length && (
          <div className="text-center py-20 text-gray-400">
            <Mountains size={48} className="mx-auto mb-4 opacity-30" />
            <p>{lang === "en" ? "Content coming soon" : "Contenido pronto disponible"}</p>
          </div>
        )}
      </div>
    </main>
  );
}
